/**
 * @file UpdatePoFromBillDialog.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-08
 * @modified 2026-10-08
 *
 * "Update PO from this bill…" for a bill already saved (Cam, 8 Oct 2026) —
 * from the bill window, Search Bills' row menu, after changing its PO links,
 * and after a bill changed in Xero (its POs were not changed). Reads what
 * differs (GET /api/bills/:id/po-cost-differences), asks with the same window
 * as a save (BillPoCostDialog), and applies the ticked lines
 * (POST /api/bills/:id/po-costs — the same writer, audited 'Cost from bill
 * <no>'; a Xero-made PO's new cost goes to Xero after it). Nothing differs:
 * it says so and closes.
 *
 * 1.2.0 (2026-10-08, Cam): bills only — a supplier credit never changes a
 * PO's cost ("Update PO from this credit…", added in 1.1.0, is gone; the
 * server answers a credit with its reason and no lines).
 */

import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import BillPoCostDialog from './BillPoCostDialog';
import AlertDialog from './AlertDialog';
import { defaultCostTicks, chosenCostLines } from '../utils/billCostWriteBack';

/**
 * @param {object} props
 * @param {number} props.billId
 * @param {() => void} props.onClose
 * @param {(message: string) => void} [props.onDone] — after lines were applied
 */
function UpdatePoFromBillDialog({ billId, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/bills/${billId}/po-cost-differences`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (!d.success) { setError(d.error || 'The PO costs could not be read.'); return; }
        setData(d);
      })
      .catch(err => { if (!cancelled) setError(`The PO costs could not be read: ${err.message}`); });
    return () => { cancelled = true; };
  }, [billId]);

  const apply = async (ticks) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/bills/${billId}/po-costs`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lines: chosenCostLines(data.differences, ticks) })
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.success) { setError(d.error || 'The PO could not be updated.'); return; }
      const pos = d.cost_write_back?.pos_updated || [];
      const left = d.cost_write_back?.cost_differences?.length || 0;
      onDone && onDone(`${pos.length ? `Updated from the bill: ${pos.join(', ')}.` : 'Updated from the bill.'}`
        + (left ? ` ${left} line${left === 1 ? '' : 's'} kept as ${left === 1 ? 'it was' : 'they were'}.` : ''));
      onClose();
    } catch (err) {
      setError(`The PO could not be updated: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (error && !data) {
    return <AlertDialog isOpen type="error" message={error} onClose={onClose} />;
  }
  if (!data) return null;
  if (!data.differences?.length) {
    return <AlertDialog isOpen type="info" message={data.block || 'This bill and its POs already agree on cost — nothing to update.'} onClose={onClose} />;
  }
  return (
    <BillPoCostDialog
      title={`Update PO from bill ${data.bill?.label || ''}`.trim()}
      changes={data.differences}
      initialTicks={defaultCostTicks(data.differences, { queried: !!data.bill?.is_queried })}
      queried={!!data.bill?.is_queried}
      busy={busy}
      error={error}
      onApply={apply}
      onKeep={onClose}
      onClose={onClose}
    />
  );
}

export default UpdatePoFromBillDialog;
